import { useEffect, useState, useRef } from 'react';
import { Briefcase, ChevronDown, Check, RefreshCw } from 'lucide-react';
import { useProjectStore } from '../stores/projectStore';
import { useAuthStore } from '../stores/authStore';
import { useQueryClient } from '@tanstack/react-query';

export function ProjectSwitcher() {
  const { user } = useAuthStore();
  const { activeProjectId, projects, loading, refresh, setActiveProject } =
    useProjectStore();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const qc = useQueryClient();

  useEffect(() => {
    if (user) refresh();
  }, [user, refresh]);

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    if (open) document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  if (!user) return null;

  const active = projects.find((p) => p.id === activeProjectId) ?? null;
  const label = active ? active.name : 'All projects';

  function pick(id: number | null) {
    setActiveProject(id);
    setOpen(false);
    qc.invalidateQueries();
  }

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-dark-800 border border-dark-700 hover:border-primary-500 transition-colors text-sm text-white"
        title="Active project"
      >
        <Briefcase className="w-4 h-4 text-primary-400" />
        <span className="max-w-[180px] truncate">{label}</span>
        {active && (
          <span className="text-xs text-dark-400">
            · {active.inverter_count} inv
          </span>
        )}
        <ChevronDown className={`w-3.5 h-3.5 text-dark-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-1 w-72 bg-dark-900 border border-dark-700 rounded-md shadow-2xl z-50 max-h-96 overflow-y-auto">
          <div className="flex items-center justify-between px-3 py-2 border-b border-dark-700">
            <span className="text-xs uppercase tracking-wide text-dark-400">Project</span>
            <button
              onClick={() => refresh()}
              className="text-dark-400 hover:text-white"
              title="Refresh"
              disabled={loading}
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>

          <button
            onClick={() => pick(null)}
            className={`w-full text-left px-3 py-2 hover:bg-dark-800 flex items-center justify-between text-sm ${
              activeProjectId == null ? 'text-primary-400' : 'text-white'
            }`}
          >
            <span>All projects</span>
            {activeProjectId == null && <Check className="w-4 h-4" />}
          </button>

          <div className="border-t border-dark-700">
            {projects.length === 0 && !loading && (
              <div className="px-3 py-3 text-xs text-dark-400">
                No projects yet — create one in Data Ingestion.
              </div>
            )}
            {projects.map((p) => (
              <button
                key={p.id}
                onClick={() => pick(p.id)}
                className={`w-full text-left px-3 py-2 hover:bg-dark-800 flex items-center justify-between text-sm ${
                  p.id === activeProjectId ? 'text-primary-400' : 'text-white'
                }`}
              >
                <div className="min-w-0 flex-1">
                  <div className="font-medium truncate">{p.name}</div>
                  <div className="text-xs text-dark-400 truncate">
                    {p.client ? `${p.client} · ` : ''}
                    {p.inverter_count} inv · {p.import_count} imports
                  </div>
                </div>
                {p.id === activeProjectId && <Check className="w-4 h-4 ml-2 flex-shrink-0" />}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
